// biome-ignore-all lint/style/useNamingConvention: Borg stream payload keys are snake_case

import type { Record } from "@suse-o11y/record";

import { borgMessageSchema } from "../models/borg-schemas";
import type {
	BorgStreamEnvelope,
	BorgStreamEvent,
	BorgStreamEventMetadata,
	BorgStreamEventType,
} from "../models/borg-stream-types";

export interface ParsedBorgStreamEvent {
	event: BorgStreamEvent;
	metadata?: BorgStreamEventMetadata;
}

const isObjectRecord = (value: unknown): value is Record<string, unknown, "writable"> =>
	typeof value === "object" && value != null && !Array.isArray(value);

const toEmittedAtMs = (value: number | string | undefined): number | undefined => {
	if (typeof value === "number") {
		return Number.isFinite(value) ? value : undefined;
	}
	if (typeof value === "string") {
		const parsed = Date.parse(value);
		return Number.isNaN(parsed) ? undefined : parsed;
	}
	return undefined;
};

const isBorgStreamEnvelope = (
	value: unknown,
	expectedEventType: string,
): value is BorgStreamEnvelope & { event_type: BorgStreamEventType } => {
	if (!isObjectRecord(value)) {
		return false;
	}
	if (typeof value.schema_version !== "number" || typeof value.event_type !== "string") {
		return false;
	}
	if (value.event_type !== expectedEventType) {
		return false;
	}
	return isObjectRecord(value.payload);
};

export function parseBorgStreamEvent(eventType: string, data: string): ParsedBorgStreamEvent | undefined {
	try {
		const parsed = JSON.parse(data) as unknown;
		if (isBorgStreamEnvelope(parsed, eventType)) {
			const snapshotMessage =
				parsed.event_type === "snapshot" ? borgMessageSchema.safeParse(parsed.payload.current_message) : undefined;
			if (snapshotMessage != null && !snapshotMessage.success) {
				return undefined;
			}
			const metadata: BorgStreamEventMetadata = {
				schemaVersion: parsed.schema_version,
				conversationId: parsed.conversation_id,
				sequence: parsed.seq,
				attempt: parsed.attempt,
				emittedAt: toEmittedAtMs(parsed.emitted_at),
			};
			const event: BorgStreamEvent =
				snapshotMessage == null
					? ({ type: parsed.event_type, ...parsed.payload } as BorgStreamEvent)
					: { type: "snapshot", current_message: snapshotMessage.data };
			return {
				event,
				metadata,
			};
		}
		if (!isObjectRecord(parsed)) {
			return undefined;
		}
		// Envelope-like objects with mismatched/invalid metadata are rejected
		// rather than treated as legacy v1 payloads.
		if ("schema_version" in parsed && "event_type" in parsed && "payload" in parsed) {
			return undefined;
		}
		if (eventType === "snapshot") {
			const snapshotMessage = borgMessageSchema.safeParse(parsed.current_message);
			if (!snapshotMessage.success) {
				return undefined;
			}
			return { event: { type: "snapshot", current_message: snapshotMessage.data } };
		}
		return { event: { type: eventType, ...parsed } as BorgStreamEvent };
	} catch {
		return undefined;
	}
}
